/**
 * Configuracion del menu lateral del sistema.
 *
 * Cada item indica:
 *  - id: ruta de React Router
 *  - label: texto visible
 *  - icon: nombre del icono de Material Icons
 *  - roles: roles que pueden ver el item (vacio = todos los autenticados)
 *
 * Cuando se implementen pantallas, las rutas deben coincidir con estos `path`.
 */
import type { UserRole } from '../context/AuthContext';

export interface MenuItem {
  path: string;
  label: string;
  icon: string;
  /** Si se especifica, solo los roles listados ven el item */
  roles?: UserRole[];
}

export interface MenuGroup {
  group: string;
  items: MenuItem[];
}

export const menuConfig: MenuGroup[] = [
  {
    group: 'Principal',
    items: [
      { path: '/', label: 'Dashboard', icon: 'dashboard' },
    ],
  },
  {
    group: 'Administracion',
    items: [
      {
        path: '/empresas',
        label: 'Empresas',
        icon: 'business',
        roles: ['admin_general', 'operador'], // Empresa NO ve listado
      },
      {
        path: '/mi-empresa',
        label: 'Mi Empresa',
        icon: 'business',
        roles: ['admin_empresa'], // Solo empresa
      },
      { path: '/afiliados', label: 'Afiliados', icon: 'people' },
      {
        path: '/tramites',
        label: 'Trámites de afiliados',
        icon: 'inbox',
        roles: ['admin_general', 'operador'],
      },
      {
        path: '/periodos',
        label: 'Periodos',
        icon: 'event',
        roles: ['admin_general', 'operador'],
      },
      { path: '/ddjj', label: 'Declaraciones Juradas', icon: 'description' },
      {
        path: '/usuarios',
        label: 'Usuarios',
        icon: 'manage_accounts',
        roles: ['admin_general'],
      },
    ],
  },
  {
    group: 'Finanzas',
    items: [
      { path: '/cupones', label: 'Cupones', icon: 'receipt_long' },
      { path: '/pagos', label: 'Pagos', icon: 'payments' },
      {
        path: '/cuenta-corriente',
        label: 'Cuenta Corriente',
        icon: 'account_balance_wallet',
        roles: ['admin_general', 'operador'],  // empresa entra a /mi-cuenta-corriente
      },
      {
        path: '/mi-cuenta-corriente',
        label: 'Mi cuenta corriente',
        icon: 'account_balance_wallet',
        roles: ['admin_empresa'],
      },
      {
        path: '/transacciones',
        label: 'Transacciones',
        icon: 'swap_horiz',
        roles: ['admin_general', 'operador'],
      },
      {
        path: '/cvus',
        label: 'Gestion CVUs',
        icon: 'vpn_key',
        roles: ['admin_general'],
      },
      {
        path: '/acreditacion',
        label: 'Acreditacion Automatica',
        icon: 'upload_file',
        roles: ['admin_general', 'operador'],
      },
    ],
  },
  {
    group: 'Reportes',
    items: [
      {
        path: '/reportes',
        label: 'Reportes',
        icon: 'assessment',
        // Admin empresa ve sus datos via /mi-cuenta-corriente; los reportes
        // tabulares son de uso interno (admin general + operador). Matriz
        // de permisos del requerimiento M13.
        roles: ['admin_general', 'operador'],
      },
    ],
  },
  {
    group: 'Configuracion',
    items: [
      {
        path: '/configuracion/parametros',
        label: 'Parametros',
        icon: 'tune',
        roles: ['admin_general'],
      },
      {
        path: '/configuracion/conceptos-aporte',
        label: 'Conceptos de aporte',
        icon: 'savings',
        roles: ['admin_general'],
      },
      {
        path: '/configuracion/catalogos/categorias',
        label: 'Categorias',
        icon: 'category',
        roles: ['admin_general'],
      },
      {
        path: '/configuracion/catalogos/provincias',
        label: 'Provincias',
        icon: 'map',
        roles: ['admin_general'],
      },
      {
        path: '/configuracion/catalogos/localidades',
        label: 'Localidades',
        icon: 'location_city',
        roles: ['admin_general'],
      },
      {
        path: '/configuracion/catalogos/parentescos',
        label: 'Parentescos',
        icon: 'family_restroom',
        roles: ['admin_general'],
      },
      {
        path: '/configuracion/auditoria',
        label: 'Auditoria',
        icon: 'history',
        roles: ['admin_general'],
      },
      { path: '/cambiar-contrasena', label: 'Cambiar Contrasena', icon: 'lock' },
    ],
  },
];

/**
 * Filtra los items del menu segun el rol del usuario
 */
export function getMenuForRole(rol: UserRole): MenuGroup[] {
  return menuConfig
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.roles || item.roles.includes(rol)),
    }))
    .filter((group) => group.items.length > 0);
}

/**
 * Etiqueta legible de un rol
 */
export function getRoleLabel(rol: UserRole): string {
  switch (rol) {
    case 'admin_general':
      return 'Admin General';
    case 'operador':
      return 'Operador';
    case 'admin_empresa':
      return 'Admin Empresa';
    default:
      return rol;
  }
}
